// components/Navigation.tsx
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '../auth/AuthProvider';
import { useLocation, useNavigate } from 'react-router-dom';

export function Navigation() {
  const { user, signOut, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { id: 'home', label: 'Home', to: '/' },
    { id: 'courses', label: 'Courses', to: '/courses' },
    { id: 'classroom', label: 'Classroom', to: '/classroom' },
    { id: 'about', label: 'About', to: '/about' },
    { id: 'contact', label: 'Contact', to: '/contact' },
  ];

  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // measure nav height for --nav-h
  const navRef = useRef<HTMLDivElement>(null);
  const setNavHeightVar = () => {
    const h = navRef.current?.offsetHeight ?? 0;
    document.documentElement.style.setProperty('--nav-h', `${h}px`);
  };
  useLayoutEffect(() => {
    setNavHeightVar();
  }, []);
  useEffect(() => {
    const onResize = () => setNavHeightVar();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    setNavHeightVar();
  }, [menuOpen, isMobile, loading, user, location.pathname]);

  const go = (to: string) => {
    navigate(to);
    setMenuOpen(false);
  };

  const path = location.pathname;

  const isActive = (itemId: string, to: string) => {
    if (itemId === 'home') return path === '/';
    return path.startsWith(to);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 left-0 right-0 z-50 px-6 py-4"
      style={{ position: 'sticky', top: 0, zIndex: 50 }}
    >
      <div className="max-w-7xl mx-auto" ref={navRef}>
        <div
          className="rounded-full px-4 md:px-8 py-3 md:py-4 flex items-center justify-between"
          style={{
            background: 'rgba(15, 35, 60, 0.7)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(0, 229, 209, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(0, 229, 209, 0.1)',
          }}
        >
          {/* Brand → home */}
          <button
            onClick={() => go('/')}
            className="flex items-center gap-2 select-none"
            aria-label="EdgeAI Academy Home"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
              <span className="text-white">EA</span>
            </div>
            <span className="text-white">EdgeAI Academy</span>
          </button>

          {/* Desktop nav + auth */}
          {!isMobile && (
            <div className="flex items-center gap-8">
              <div className="flex gap-8">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => go(item.to)}
                    className="relative text-white transition-colors hover:text-cyan-400"
                  >
                    {item.label}
                    {isActive(item.id, item.to) && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-cyan-400"
                        style={{ boxShadow: '0 0 10px rgba(0, 229, 209, 0.8)' }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {!loading && (
                <div className="flex items-center gap-3">
                  {!user ? (
                    <>
                      <button
                        onClick={() => go('/signin')}
                        className="relative text-white transition-colors hover:text-cyan-400"
                      >
                        Sign in
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => go('/account')}
                        className="px-4 py-2 rounded-full text-white/90 hover:text-white border border-cyan-400/40 hover:border-cyan-400/70 transition"
                      >
                        Account
                      </button>
                      <button
                        onClick={() => {
                          try {
                            signOut?.();
                          } finally {
                            go('/');
                          }
                        }}
                        className="px-4 py-2 rounded-full text-white/80 hover:text-white transition"
                      >
                        Sign out
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mobile hamburger (unchanged visually) */}
          {isMobile && (
            <button
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition
                ${
                  menuOpen
                    ? 'bg-white/15 text-white border border-white/30'
                    : 'bg-black/20 text-white border border-white/20 hover:border-white/40'
                }`}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                {menuOpen ? (
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                ) : (
                  <path
                    d="M3 6h18M3 12h18M3 18h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            </button>
          )}
        </div>

        {/* Mobile dropdown */}
        {isMobile && menuOpen && (
          <div
            className="mt-3 rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(6, 16, 28, 0.96)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.35)',
            }}
          >
            <div className="flex flex-col py-2">
              {navItems.map((item) => {
                const active = isActive(item.id, item.to);
                return (
                  <button
                    key={item.id}
                    onClick={() => go(item.to)}
                    className={`text-white text-left px-4 py-3 rounded-lg mx-2 my-1 transition
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60
                      ${active ? 'bg-white/12' : 'bg-white/0 hover:bg-white/10'}`}
                  >
                    {item.label}
                  </button>
                );
              })}

              <div className="h-px my-2 mx-2 bg-white/10" />

              {!loading && (
                <div className="px-2 pb-2">
                  {!user ? (
                    <button
                      onClick={() => go('/signin')}
                      className="w-full text-left text-white px-4 py-3 rounded-lg transition
                                 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                      Sign in
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => go('/account')}
                        className="w-full text-left text-white px-4 py-3 rounded-lg transition
                                   hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                      >
                        Account
                      </button>
                      <button
                        onClick={() => {
                          try {
                            signOut?.();
                          } finally {
                            go('/');
                          }
                        }}
                        className="w-full mt-2 text-left text-white/90 px-4 py-3 rounded-lg transition
                                   hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                      >
                        Sign out
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.nav>
  );
}
